import React from "react";
import { withStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import { Alert } from "@material-ui/lab";
import CardMedia from "@material-ui/core/CardMedia";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import SkipPreviousIcon from "@material-ui/icons/SkipPrevious";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import SkipNextIcon from "@material-ui/icons/SkipNext";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import { Grid, TextField } from "@material-ui/core";
import {
  makePlayerRequest,
  updatePlaylistsForSong,
} from "../api_utilities/spotify";
import {
  makeBackendAPIRequest,
  backendBaseURL,
} from "../api_utilities/backend";
import Slider from "@material-ui/core/Slider";
import Snackbar from "@material-ui/core/Snackbar";
import TagDialog from "./TagDialog";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import { observer } from "mobx-react";

const useStyles = (theme) => ({
  root: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  cover: {
    width: "100%",
    height: "100%",
    backgroundSize: "contain",
  },
  noWrapText: {
    width: "100%",
  },
  playIconOrPauseIcon: {
    height: 38,
    width: 38,
  },
  inputRounded: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 50,
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  spanText: {
    width: "100%",
  },
});

const Player = observer(
  class Player extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        trackRatingError: false,
        openSnackbar: false,
        errorSnackbar: false,
        tagDialogOpen: false,
      };
    }

    componentDidMount() {
      const { store } = this.props;
      setInterval(() => store.updateCurrentlyPlayingTrack(), 3000);
    }

    pauseTrack = () => {
      const { store } = this.props;
      const url = new URL("https://api.spotify.com/v1/me/player/pause");
      makePlayerRequest(url, "PUT", store.updateCurrentlyPlayingTrack);
    };

    playTrack = () => {
      const { store } = this.props;
      const url = new URL("https://api.spotify.com/v1/me/player/play");
      makePlayerRequest(url, "PUT", store.updateCurrentlyPlayingTrack);
    };

    skipToNextTrack = () => {
      const { store } = this.props;
      const url = new URL("https://api.spotify.com/v1/me/player/next");
      makePlayerRequest(url, "POST", store.updateCurrentlyPlayingTrack);
    };

    skipToPreviousTrack = () => {
      const { store } = this.props;
      const url = new URL("https://api.spotify.com/v1/me/player/previous");
      makePlayerRequest(url, "POST", store.updateCurrentlyPlayingTrack);
    };

    seekInTrack = async (_, seekPosition) => {
      await this.updatePlayerSeekPosition(seekPosition / 100);
    };

    updatePlayerSeekPosition = AwesomeDebouncePromise((newPosition) => {
      const { store } = this.props;

      const url = new URL("https://api.spotify.com/v1/me/player/seek");
      url.searchParams.append(
        "position_ms",
        Math.round(newPosition * store.currentlyPlayingTrack.trackLength)
      );
      makePlayerRequest(url, "PUT", store.updateCurrentlyPlayingTrack);
    }, 250);

    rateTrack = (event) => {
      const newValue = event.target.value;

      if (newValue !== "-" && isNaN(newValue)) {
        this.setState({ trackRatingError: true });
        return;
      }

      const { store } = this.props;

      store.rateCurrentlyPlayingTrack(newValue);

      const url = new URL(backendBaseURL + "/song/rate_song");
      makeBackendAPIRequest({
        url: url,
        method: "POST",
        body: {
          id_song_spotify: store.currentlyPlayingTrack.trackID,
          id_album_spotify: store.currentlyPlayingTrack.albumID,
          album_total_tracks: store.currentlyPlayingTrack.albumTotalTracks,
          rating: newValue,
          album_name: store.currentlyPlayingTrack.albumName,
          release_year: store.currentlyPlayingTrack.releaseYear,
          album_art_url: store.currentlyPlayingTrack.albumArtURL,
          album_artists: store.currentlyPlayingTrack.artistNames,
          song_name: store.currentlyPlayingTrack.trackName,
          duration_ms: store.currentlyPlayingTrack.trackLength,
          track_number: store.currentlyPlayingTrack.trackNumber,
        },
        onSuccessCallback: (data) => {
          this.setState({ trackRatingError: false, openSnackbar: true });

          updatePlaylistsForSong(
            store.currentlyPlayingTrack.trackID,
            data.add_to_playlists,
            data.remove_from_playlists
          );
        },
        onFailureCallback: () => {
          this.setState({
            trackRatingError: true,
            openSnackbar: true,
            errorSnackbar: true,
          });
        },
      });
    };

    addTags = () => {
      this.setState({ tagDialogOpen: true });
    };

    tagDialogOnClose = () => {
      this.setState({ tagDialogOpen: false });
    };

    formatSecondsAsTime = (seconds) => {
      if (!seconds) {
        return "";
      }
      return `${Math.floor(seconds / 60)}:${seconds % 60 < 10 ? "0" : ""}${
        seconds % 60
      }`;
    };

    render() {
      const { classes, store } = this.props;
      const { trackRatingError, openSnackbar, errorSnackbar, tagDialogOpen } =
        this.state;

      return (
        <Card className={classes.root}>
          <Grid container spacing={3}>
            <Grid container item spacing={1} xs={3}>
              <Grid container item justify="center" direction="column" xs={3}>
                {store.currentlyPlayingTrack.albumArtURL ? (
                  <CardMedia
                    className={classes.cover}
                    image={store.currentlyPlayingTrack.albumArtURL}
                    title={
                      store.currentlyPlayingTrack.albumName + " album cover"
                    }
                  />
                ) : (
                  <div></div>
                )}
              </Grid>
              <Grid container item justify="center" direction="column" xs={9}>
                <Typography
                  variant="subtitle1"
                  noWrap
                  className={classes.noWrapText}
                >
                  {store.currentlyPlayingTrack.trackName}
                </Typography>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  noWrap
                  className={classes.noWrapText}
                >
                  {store.currentlyPlayingTrack.artistNames}
                </Typography>
              </Grid>
            </Grid>
            <Grid container item justify={"center"} xs={6}>
              <Grid item xs={3} />
              <Grid container item justify={"space-around"} xs={6}>
                <IconButton onClick={this.skipToPreviousTrack}>
                  <SkipPreviousIcon />
                </IconButton>
                {store.isCurrentlyPlaying ? (
                  <IconButton onClick={this.pauseTrack}>
                    <PauseIcon className={classes.playIconOrPauseIcon} />
                  </IconButton>
                ) : (
                  <IconButton onClick={this.playTrack}>
                    <PlayArrowIcon className={classes.playIconOrPauseIcon} />
                  </IconButton>
                )}
                <IconButton onClick={this.skipToNextTrack}>
                  <SkipNextIcon />
                </IconButton>
              </Grid>
              <Grid item xs={3} />
              <Grid container item xs={12} spacing={1} alignItems="center">
                <Grid container item xs={1}>
                  <Typography
                    align="left"
                    className={classes.spanText}
                    variant="caption"
                    color={"textSecondary"}
                  >
                    {this.formatSecondsAsTime(
                      Math.round(
                        store.currentlyPlayingTrack.trackProgress / 1000
                      )
                    )}
                  </Typography>
                </Grid>
                <Grid container item xs={10}>
                  <Slider
                    value={
                      (store.currentlyPlayingTrack.trackProgress /
                        store.currentlyPlayingTrack.trackLength) *
                      100
                    }
                    onChange={this.seekInTrack}
                  />
                </Grid>
                <Grid container item xs={1}>
                  <Typography
                    align="right"
                    className={classes.spanText}
                    variant="caption"
                    color={"textSecondary"}
                  >
                    {this.formatSecondsAsTime(
                      Math.round(store.currentlyPlayingTrack.trackLength / 1000)
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid container item xs={3}>
              <Grid item xs={10} />
              <Grid item xs={2}>
                <TextField
                  disabled={store.currentlyPlayingTrack.trackID == null}
                  error={trackRatingError}
                  variant="outlined"
                  size="small"
                  className={classes.inputRounded}
                  onChange={this.rateTrack}
                  value={store.currentlyPlayingTrack.rating}
                  inputProps={{ style: { textAlign: "center" } }}
                />
              </Grid>
              <Grid item xs={10} />
              <Grid item xs={2}>
                <IconButton
                  onClick={this.addTags}
                  disabled={store.currentlyPlayingTrack.trackID == null}
                >
                  {store.currentlyPlayingTrack.tags &&
                  store.currentlyPlayingTrack.tags.length > 0 ? (
                    <LocalOfferIcon
                      color={
                        store.currentlyPlayingTrack.trackID != null
                          ? "primary"
                          : "disabled"
                      }
                    />
                  ) : (
                    <LocalOfferOutlinedIcon
                      color={
                        store.currentlyPlayingTrack.trackID != null
                          ? "primary"
                          : "disabled"
                      }
                    />
                  )}
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
          <Snackbar
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            open={openSnackbar}
            autoHideDuration={3000}
            onClose={() => {
              this.setState({ openSnackbar: false });
            }}
          >
            {errorSnackbar ? (
              <Alert
                onClose={() => {
                  this.setState({ openSnackbar: false });
                }}
                severity="error"
              >
                Failed to Save Track Rating!
              </Alert>
            ) : (
              <Alert
                onClose={() => {
                  this.setState({ openSnackbar: false });
                }}
                severity="success"
              >
                Track Rating Saved!
              </Alert>
            )}
          </Snackbar>
          <TagDialog
            trackAndAlbumInfo={{
              albumArtists: store.currentlyPlayingTrack.artistNames,
              albumArtURL: store.currentlyPlayingTrack.albumArtURL,
              albumID: store.currentlyPlayingTrack.albumID,
              albumName: store.currentlyPlayingTrack.albumName,
              albumTotalTracks: store.currentlyPlayingTrack.albumTotalTracks,
              releaseYear: store.currentlyPlayingTrack.releaseYear,
              tags: store.currentlyPlayingTrack.tags,
              trackID: store.currentlyPlayingTrack.trackID,
              trackName: store.currentlyPlayingTrack.trackName,
              trackLength: store.currentlyPlayingTrack.trackLength,
              trackNumber: store.currentlyPlayingTrack.trackNumber,
            }}
            isOpen={tagDialogOpen}
            onClose={this.tagDialogOnClose}
            onTagsUpdatedOptimistic={() => {}}
            store={store}
          />
        </Card>
      );
    }
  }
);

export default withStyles(useStyles)(Player);
